import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import NewCustomerJourney from "@/pages/NewCustomerJourney";
import CustomerDashboard from "@/pages/CustomerDashboard";
import ChefDashboard from "@/pages/ChefDashboard";
import Personas from "@/pages/Personas";
import AdminDashboard from "@/pages/AdminDashboard";
import ForgotPassword from "@/pages/ForgotPassword";
import ChangePassword from "@/pages/ChangePassword";
import PersonaPreview from "@/pages/PersonaPreview";
import PersonaLogin from "@/pages/PersonaLogin";
import { Toaster } from "@/components/ui/sonner";

function CustomerGate() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "customer") return <Navigate to={user.role === "admin" ? "/admin" : "/chef"} replace />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  return <CustomerDashboard />;
}

// Persona-scoped smart gate — same URL serves both login and dashboard depending on auth state.
// Existing users are redirected to THEIR persona's home even if they landed on a different entry URL.
function PersonaGate({ intent, DashboardComponent }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <PersonaLogin intent={intent} />;
  if (user.role === intent) return <DashboardComponent />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "chef") return <Navigate to="/chef" replace />;
  return <Navigate to="/app" replace />;
}

// Detect OAuth callback in URL fragment FIRST, before any other routing.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<AuthPage />} />
      <Route path="/personas" element={<Personas />} />
      <Route path="/register" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/preview" element={<PersonaPreview />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute roles={["customer"]}><NewCustomerJourney /></ProtectedRoute>} />
      <Route path="/app" element={<CustomerGate />} />
      {/* Three self-contained OTP entry points. Each doubles as login-or-dashboard. */}
      <Route path="/customer" element={<PersonaGate intent="customer" DashboardComponent={CustomerGate} />} />
      <Route path="/chef" element={<PersonaGate intent="chef" DashboardComponent={ChefDashboard} />} />
      <Route path="/admin" element={<PersonaGate intent="admin" DashboardComponent={AdminDashboard} />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <div className="App">
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}
